import { ResponsiveDialog, signOut } from "@brandclub/common-ui";
import { Alert, Box, CircularProgress, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  resendContactEmailVerificationCode,
  updateProfile,
  updateProfilePhotoUrl,
  uploadUserProfilePhoto,
  verifyContactEmail,
} from "../../../../../api";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { setUserProfile } from "../../../../../redux/reducers/userProfile";
import { formatPhone } from "../../../../../utils/StringUtils";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../../../../AppNavigation/constants";
import {
  ButtonLoadingEllipsis,
  OutlineButton,
  SolidButton,
  TrackedMotionButton,
} from "../../../../StoreComponents/StoreButton";
import StoreDatePicker from "../../../../StoreComponents/StoreDatePicker";
import { StoreError } from "../../../../StoreComponents/StoreError";
import StoreInput from "../../../../StoreComponents/StoreInput";
import StoreSelect from "../../../../StoreComponents/StoreSelect";
import { DashboardHeaderContainer } from "../DashboardHeaderContainer";
import ProfilePhoto from "./ProfilePhoto";

export const ProfileContainer = styled("div")(({ theme }) => ({
  ".profile_header_container": {
    // height: 44,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    profile_avatar: { marginRight: 20 },
    ".profile_header": {
      flex: 1,
      fontWeight: 700,
      fontSize: 16,
      color: theme.vars.palette.text.primary,
      [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        fontSize: 22,
      },
    },
    ".header_action": {
      a: {
        textDecoration: "none",
      },
    },
  },

  ".fields_section_header": {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: 20,
    color: theme.vars.palette.text.primary,
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 16,
    },
  },
  ".profile_fields": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: "34px",
    rowGap: "20px",
    [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gridTemplateColumns: "repeat(1, 1fr)",
    },
  },
  ".profile_action": {
    marginTop: 30,
    display: "flex",
    alignItems: "center",

    gap: 15,
  },
  ".error": {
    color: "#E57373",
  },
}));

const EmailVerifyDialogContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px 40px 55px 40px",
  color: theme.vars.palette.text.primary,

  maxWidth: 480,
  width: "100%",
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    padding: "22px 0px 14px 0px",
  },
  ".title": {
    fontSize: 26,
    fontWeight: 700,
    marginBottom: 16,
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
    },
  },
  ".subtitle": {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 24,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
      marginBottom: 20,
    },
  },
  ".input-container": {
    width: "100%",
    marginBottom: 20,
  },
  ".actions": {
    width: "100%",
    marginBottom: 24,
  },
  ".resend-text": {
    fontSize: 14,
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: 13,
    },
    ".resend-link": {
      color: theme.vars.palette.text.primary,
      textDecoration: "underline",
      cursor: "pointer",
      "&:hover": {
        opacity: 0.7,
      },
      "&.disabled": {
        cursor: "not-allowed",
      },
    },
  },
}));

const SignOutButton = () => {
  return (
    <SolidButton
      color="tertiary"
      onClick={async () => {
        await signOut();
      }}
      sx={{
        width: "143px",
        maxWidth: "143px",
        minWidth: "fit-content",
      }}
    >
      Sign out
    </SolidButton>
  );
};
const Profile = () => {
  const tabletView = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] =
    useState<Date | string | null | undefined>();
  const [gender, setGender] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | undefined>(undefined);

  const [error, setError] = useState("");

  // Email OTP verification state
  const [showEmailOtpDialog, setShowEmailOtpDialog] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [emailOtpError, setEmailOtpError] = useState("");
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  const [resendCoolDown, setResendCoolDown] = useState(0);

  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);

  const fieldEnterCallback = () => {
    setError("");
    setSaved(false);
  };
  const initFields = () => {
    const { family_name, given_name, birthdate, email, phoneNumber, gender } =
      userProfile as any;

    const dob = moment(birthdate).toDate();
    setFirstName(given_name);
    setLastName(family_name);
    setContactEmail(email);
    setPhoneNumber(phoneNumber);
    setDateOfBirth(dob);
    setGender(gender);
    setProfilePhoto(undefined);
  };

  const cancelClick = () => {
    initFields();
  };

  useEffect(() => {
    if (userProfile) {
      initFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCoolDown > 0) {
      const timer = setTimeout(
        () => setResendCoolDown(resendCoolDown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCoolDown]);

  const handleVerifyEmailOtp = async () => {
    if (emailOtpCode.trim() === "") return;

    setVerifyingEmailOtp(true);
    setEmailOtpError("");
    try {
      const res = await verifyContactEmail({
        emailVerificationCode: emailOtpCode,
      });

      if (res.status === "success") {
        // Email verified successfully
        setShowEmailOtpDialog(false);
        setEmailOtpCode("");
        setSaved(true);
        // Force reload the profile to get updated user info
        window.location.reload();
      } else {
        setEmailOtpError(res.errorMessage || "Invalid verification code");
      }
    } catch (e: any) {
      setEmailOtpError(e.message || "Failed to verify code");
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  const handleResendEmailOtp = async () => {
    if (resendCoolDown > 0) return;
    setResendCoolDown(30);
    try {
      await resendContactEmailVerificationCode();
    } catch (e: any) {
      setEmailOtpError(e.message || "Failed to resend code");
    }
  };

  const handleCloseEmailOtpDialog = () => {
    setShowEmailOtpDialog(false);
    setEmailOtpCode("");
    setEmailOtpError("");
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const dob = moment(dateOfBirth).format("MM/DD/YYYY");

      let newProfile = null;
      const hasChanges =
        gender !== userProfile?.gender ||
        dob !== userProfile?.birthdate ||
        firstName !== userProfile?.given_name ||
        lastName !== userProfile?.family_name ||
        contactEmail !== userProfile?.email;

      if (hasChanges) {
        const req = {
          gender: gender || "other",
          birthdate: dob,
          given_name: firstName,
          family_name: lastName,
          name: `${firstName} ${lastName}`,
          email: contactEmail,
        };

        const res = await updateProfile(req);
        newProfile = res?.data?.result;

        // Check if email verification is required (like mobile app does)
        if (newProfile?.emailVerified === false) {
          setShowEmailOtpDialog(true);
          setResendCoolDown(30);
          dispatch(setUserProfile(newProfile));
          setSaving(false);
          return; // Wait for OTP verification
        }
      }

      if (profilePhoto) {
        const { path } = await uploadUserProfilePhoto({ file: profilePhoto });
        const res = await updateProfilePhotoUrl({ path });

        if (res?.data.status === "failed") {
        } else {
          newProfile = res.data.user;
        }
      }

      if (newProfile) {
        dispatch(setUserProfile(newProfile));
      }
      setSaved(true);
    } catch (e: any) {
      setError(e.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    gender !== userProfile?.gender ||
    moment(dateOfBirth).format("MM/DD/YYYY") !== userProfile?.birthdate ||
    firstName !== userProfile?.given_name ||
    lastName !== userProfile?.family_name ||
    contactEmail !== userProfile?.email ||
    profilePhoto;

  const saveButtonDisabled = !canSave || saving;

  return (
    <ProfileContainer>
      <div className="profile_header_container" style={{}}>
        <div className="profile_header">
          {tabletView ? (
            <div className="profile_header">
              <DashboardHeaderContainer
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <div className="title">Personal</div>
                <SignOutButton />
              </DashboardHeaderContainer>
            </div>
          ) : (
            <DashboardHeaderContainer
              sx={{
                marginBottom: "30px",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="title">
                <ProfilePhoto
                  initImage={userProfile?.profileImageUrl || ""}
                  image={profilePhoto}
                  setImage={setProfilePhoto}
                />
                <span>Personal</span>
              </div>
              <SignOutButton />
            </DashboardHeaderContainer>
          )}
        </div>
      </div>
      <div className="profile_fields">
        <StoreInput
          label="First name"
          value={firstName}
          onChange={(e) => {
            fieldEnterCallback();
            setFirstName(e.target.value);
          }}
          sx={{ maxWidth: "100%" }}
        />
        <StoreInput
          label="Last name"
          value={lastName}
          onChange={(e) => {
            fieldEnterCallback();
            setLastName(e.target.value);
          }}
          sx={{ maxWidth: "100%" }}
        />
        <StoreInput
          label="Contact email"
          value={contactEmail}
          onChange={(e) => {
            fieldEnterCallback();
            setContactEmail(e.target.value);
          }}
          sx={{ maxWidth: "100%" }}
        />
        <StoreInput
          disabled={true}
          label="Phone number"
          value={formatPhone(phoneNumber || "", "national")}
          onChange={(e) => {}}
          sx={{ maxWidth: "100%" }}
        />

        <StoreDatePicker
          value={dateOfBirth}
          onChange={(v) => {
            fieldEnterCallback();
            setDateOfBirth(v);
          }}
          label={"Date of birth"}
          inputProps={{
            sx: { maxWidth: "100%" },
          }}
          // showCalendarIcon
        />

        <StoreSelect
          label={"Gender"}
          value={gender}
          onChange={(e) => {
            fieldEnterCallback();
            setGender(e.target.value as string);
          }}
          items={[
            { displayName: "Male", value: "male" },
            { displayName: "Female", value: "female" },
            { displayName: "Other", value: "other" },
          ]}
          fullWidth
        />
      </div>
      {error ? (
        <>
          <StoreError
            sx={{
              marginTop: "10px",
              marginBottom: "0px",
              justifyContent: "center",
            }}
            errorMessage={error}
          />
        </>
      ) : null}
      <div className="profile_action">
        <OutlineButton disabled={!canSave || saving} onClick={cancelClick}>
          Cancel
        </OutlineButton>
        <div style={{ flex: 1 }}></div>
        <TrackedMotionButton
          color="primary"
          variant={saveButtonDisabled ? "outline" : "solid"}
          disabled={saveButtonDisabled}
          onClick={handleSave}
        >
          {saved ? (
            "Saved!"
          ) : saving ? (
            <>
              Saving
              <ButtonLoadingEllipsis baseFontSize={12} />
            </>
          ) : (
            "Save"
          )}
        </TrackedMotionButton>
      </div>

      {/* Email OTP Verification Dialog */}
      <ResponsiveDialog
        open={showEmailOtpDialog}
        disableScrollLock
        onClose={handleCloseEmailOtpDialog}
        sx={(theme) => ({
          zIndex: 1400,
          "& div.MuiDialog-paper": {
            margin: "0 20px",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            [theme.breakpoints.down("md")]: {
              maxWidth: "100%",
              margin: "0px 0px",
              padding: "16px 16px",
              boxSizing: "border-box",
            },
          },
        })}
      >
        <EmailVerifyDialogContainer>
          <div className="title">Verify your email</div>
          <div className="subtitle">
            Please enter your verification code here.
          </div>

          <div className="input-container">
            <StoreInput
              value={emailOtpCode}
              onChange={(e) => {
                setEmailOtpError("");
                setEmailOtpCode(e.target.value);
              }}
              placeholder="Verification code"
              errorMsg={emailOtpError}
              sx={{ width: "100%", maxWidth: "100%" }}
              inputProps={{
                inputMode: "numeric",
              }}
            />
          </div>

          {emailOtpError && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {emailOtpError}
            </Alert>
          )}

          <div className="actions">
            <SolidButton
              onClick={handleVerifyEmailOtp}
              disabled={emailOtpCode.trim() === "" || verifyingEmailOtp}
              sx={{
                width: "100%",
                height: 52,
                borderRadius: "100px",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {verifyingEmailOtp ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Verifying...
                </>
              ) : (
                "Continue"
              )}
            </SolidButton>
          </div>

          <div className="resend-text">
            Didn't receive a code? Click here to{" "}
            <span
              className={`resend-link ${
                resendCoolDown > 0 || verifyingEmailOtp ? "disabled" : ""
              }`}
              onClick={() => {
                if (resendCoolDown <= 0 && !verifyingEmailOtp) {
                  handleResendEmailOtp();
                }
              }}
            >
              {resendCoolDown > 0 ? `resend (${resendCoolDown}s)` : "resend"}
            </span>
            .
          </div>
        </EmailVerifyDialogContainer>
      </ResponsiveDialog>
    </ProfileContainer>
  );
};

export default Profile;
